import { graphql } from "@acto/gql";
import type { DateRangeInput } from "@acto/gql/graphql";
import { useQuery } from "urql";
import { useComparisonModeContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { toGqlKpiComparisonMode } from "@acto/taskpane/core/data-access/customers/kpiTransformers";
import type { WorkflowKPI } from "./types";

const WorkflowKpisQuery = graphql(`
  query GetWorkflowKpis($dateRange: DateRangeInput!, $comparisonMode: KpiComparisonMode!) {
    workflowKpis(dateRange: $dateRange, comparisonMode: $comparisonMode) {
      dateRanges {
        current {
          startDate
          endDate
        }
        previous {
          startDate
          endDate
        }
      }
      todosCreated
      todosCreatedChange
      todosCompleted
      todosCompletedChange
      todosDeleted
      todosDeletedChange
      overdueTodos
      overdueTodosChange
    }
  }
`);

interface UseWorkflowKpisOptions {
  dateRange: DateRangeInput;
}

export function useWorkflowKpis({ dateRange }: UseWorkflowKpisOptions): {
  kpis: WorkflowKPI | null;
  isPending: boolean;
  isFetching: boolean;
} {
  const comparisonMode = toGqlKpiComparisonMode(useComparisonModeContext());
  const [result] = useQuery({
    query: WorkflowKpisQuery,
    variables: { dateRange, comparisonMode },
    requestPolicy: "cache-first",
  });

  const { data, fetching: isFetching } = result;
  const isPending = !data && isFetching;

  if (!data?.workflowKpis) {
    return { kpis: null, isPending, isFetching };
  }

  const kpis = data.workflowKpis;

  return {
    kpis: {
      dateRanges: kpis.dateRanges,
      todosCreated: kpis.todosCreated,
      todosCreatedChange: kpis.todosCreatedChange ?? null,
      todosCompleted: kpis.todosCompleted,
      todosCompletedChange: kpis.todosCompletedChange ?? null,
      todosDeleted: kpis.todosDeleted,
      todosDeletedChange: kpis.todosDeletedChange ?? null,
      overdueTodos: kpis.overdueTodos,
      overdueTodosChange: kpis.overdueTodosChange ?? null,
    },
    isPending,
    isFetching,
  };
}
